<template>
	<div class="catchment-area-definition" ref="zoneRef">
		<div class="catchment-area-definition__header">
			<div class="d-flex flex-column">
				<span class="title">{{ t('ComparedStores') }}</span>
				<span class="text-caption text-medium-emphasis">{{ t('ComparedStoresDescription') }}</span>
			</div>
			<v-chip size="small" variant="tonal" readonly>{{ comparedStores.length }} {{ t('ComparedStores').toLowerCase() }}</v-chip>
		</div>

		<v-alert v-if="comparedStores.length > 5" type="warning" variant="tonal" density="compact" class="catchment-area-definition__alert">{{ t('TooManyComparedStoresWarning') }}</v-alert>

		<div class="catchment-area-definition__toolbar">
			<AutoCompleteControl v-model:search="searchText" :items="candidateOptions" :label="t('Search')" title-key="label" :subtitle-key="candidateSubtitle" item-value="id" class="catchment-area-definition__search-wrap" @select="(item) => selectCandidate(item.id)" />
			<v-badge location="top right" color="primary" :offset-x="1" :offset-y="4" :model-value="filterChips.length > 0" dot dot-size="9">
				<v-btn ref="filtersButtonRef" icon="filter_alt" variant="tonal" color="primary" density="comfortable" />
			</v-badge>
			<v-chip :color="comparableProfileOnly ? 'primary' : undefined" :variant="comparableProfileOnly ? 'flat' : 'outlined'" @click="comparableProfileOnly = !comparableProfileOnly">{{ t('ComparableProfile') }}</v-chip>
		</div>

		<DeliveryLocationFiltersDialog :activator="filtersButtonRef" :locations="allDeliveryLocations" @filters-changed="onFiltersChanged" />

		<div v-if="loading" class="d-flex justify-center pa-8"><v-progress-circular indeterminate color="primary" /></div>
		<div v-else-if="!comparedStores.length" class="text-center pa-8 text-disabled">{{ t('NoData') }}</div>
		<template v-else>
			<AgGrid v-if="!isTablet" :row-data="comparedStores" :col-defs="columnDefs" :get-row-id="getRowId" :rowGroupPanelShow="'never'" />
			<TouchCardSimpleList v-else :row-data="comparedStores" title-field="label" badge-field="sps" chip-field="channel" :body-fields="['posType', 'group', 'city']" row-key="id" action-icon="close" action-color="error" :action-label="t('Remove')" @action="(store) => removeMember(store.id)" />
		</template>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getDeliveryLocations } from '@/api/sales/deliveryLocation';
import { getCatchmentAreaByDeliveryLocation, addCatchmentAreaMember, removeCatchmentAreaMember } from '@/api/sales/catchmentArea';
import { spsService } from '@/services/spsService';
import { deviceUtils } from '@/utils/core/deviceUtils';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import ButtonIconRenderer from '@/components/agGrid/ButtonIconRenderer.vue';
import DeliveryLocationRenderer from '@/components/agGrid/DeliveryLocationRenderer.vue';
import ChipCellRenderer from '@/components/agGrid/ChipCellRenderer.vue';
import TouchCardSimpleList from '@/components/agGrid/TouchCardSimpleList.vue';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';
import AutoCompleteControl from '@/components/controls/AutoCompleteControl.vue';

const t = useTranslator();
const zoneRef = ref(null);
const filtersButtonRef = ref(null);

const deliveryLocationId = ref(null);
const allDeliveryLocations = ref([]);
const zone = ref(null);
const loading = ref(false);

const searchText = ref('');
const comparableProfileOnly = ref(false);
const dialogFilteredIds = ref(null);
const filterChips = ref([]);

const isTablet = ref(deviceUtils.isTablet());
const onResize = () => {
	isTablet.value = deviceUtils.isTablet();
};

const ecosVM = iframeDataBridge.getEcosVM();
const channelsById = computed(() => new Map((ecosVM?.channels || []).map((entry) => [entry.id, entry.label || entry.name])));
const posTypesById = computed(() => new Map((ecosVM?.posTypes || []).map((entry) => [entry.id, entry.label || entry.name])));
const groupsById = computed(() => new Map((ecosVM?.DLGroups || []).map((entry) => [entry.id, entry.label || entry.name])));

function displayLabel(dl) {
	return dl?.companyName || dl?.label || `#${dl?.id}`;
}

const spsByDeliveryLocationId = ref(new Map());
function formatSps(storeId) {
	const entry = spsByDeliveryLocationId.value.get(storeId);
	return entry ? `${Math.round(entry.value * 10) / 10}` : '';
}

function displayCity(dl) {
	return dl?.city || dl?.town || dl?.postcode || '';
}

function clusterIdOf(dl) {
	return dl?.clusterId ?? dl?.clusteringId ?? null;
}

function toDisplayStore(dl) {
	return {
		id: dl.id,
		label: displayLabel(dl),
		sps: formatSps(dl.id),
		channel: channelsById.value.get(dl.channelId) || '',
		posType: posTypesById.value.get(dl.POSTypeId) || '',
		group: groupsById.value.get(dl.groupId) || '',
		city: displayCity(dl),
	};
}

const referenceDeliveryLocation = computed(() => allDeliveryLocations.value.find((dl) => dl.id === deliveryLocationId.value) || null);
const memberIds = computed(() => new Set(zone.value?.members || []));

watch(memberIds, async (ids) => {
	const idList = Array.from(ids);
	if (!idList.length) return;
	spsByDeliveryLocationId.value = await spsService.getSpsByDeliveryLocationIds(idList);
});

const comparedStores = computed(() => allDeliveryLocations.value.filter((dl) => memberIds.value.has(dl.id)).map(toDisplayStore));

// Candidate pool: everything in the user's own synced perimeter, minus the reference store itself
// and stores already added — searched/filtered entirely client-side (plan decision D1).
const candidatePool = computed(() => {
	const referenceClusterId = comparableProfileOnly.value ? clusterIdOf(referenceDeliveryLocation.value) : null;
	return allDeliveryLocations.value.filter((dl) => {
		if (dl.id === deliveryLocationId.value || memberIds.value.has(dl.id)) return false;
		if (dialogFilteredIds.value && !dialogFilteredIds.value.has(dl.id)) return false;
		if (comparableProfileOnly.value && clusterIdOf(dl) !== referenceClusterId) return false;
		return true;
	});
});

const candidateOptions = computed(() => {
	const query = searchText.value?.trim().toLowerCase();
	const pool = query ? candidatePool.value.filter((dl) => displayLabel(dl).toLowerCase().includes(query)) : candidatePool.value;
	return pool.slice(0, 50).map(toDisplayStore);
});

function candidateSubtitle(store) {
	return [store.channel, store.posType, store.city].filter(Boolean).join(' · ');
}

function onFiltersChanged({ filteredLocations, dirty, chips }) {
	dialogFilteredIds.value = dirty ? new Set((filteredLocations || []).map((dl) => dl.id)) : null;
	filterChips.value = chips || [];
}

async function selectCandidate(id) {
	if (id == null) return;
	await addMember(id);
}

async function addMember(memberDeliveryLocationId) {
	if (deliveryLocationId.value == null) return;
	zone.value = await addCatchmentAreaMember(deliveryLocationId.value, memberDeliveryLocationId);
}

async function removeMember(memberDeliveryLocationId) {
	if (deliveryLocationId.value == null) return;
	zone.value = await removeCatchmentAreaMember(deliveryLocationId.value, memberDeliveryLocationId);
}

function getRowId(params) {
	return String(params.data.id);
}

const columnDefs = computed(() => [
	{ headerName: t('Account'), field: 'label', minWidth: 220, cellRenderer: DeliveryLocationRenderer, cellRendererParams: (params) => ({ badge: params.data?.sps || null }) },
	{ headerName: t('Channel'), field: 'channel', minWidth: 130, cellRenderer: ChipCellRenderer, cellRendererParams: (params) => ({ label: params.value, color: 'secondary' }) },
	{ headerName: t('POSType'), field: 'posType', minWidth: 150 },
	{ headerName: t('Group'), field: 'group', minWidth: 150 },
	{ headerName: t('City'), field: 'city', minWidth: 130 },
	{
		headerName: '',
		colId: 'remove',
		maxWidth: 75,
		sortable: false,
		filter: false,
		enableRowGroup: false,
		cellRenderer: ButtonIconRenderer,
		cellRendererParams: (params) => ({ icon: 'close', tooltip: t('Remove'), key: params.data.id, color: 'error', onClick: () => removeMember(params.data.id) }),
		suppressHeaderMenuButton: true,
	},
]);

async function loadZone() {
	if (deliveryLocationId.value == null) return;
	loading.value = true;
	try {
		const [locations, currentZone] = await Promise.all([getDeliveryLocations(), getCatchmentAreaByDeliveryLocation(deliveryLocationId.value)]);
		allDeliveryLocations.value = locations || [];
		zone.value = currentZone;
	} finally {
		loading.value = false;
	}
}

function handleContextChange(event) {
	const dlId = event?.detail?.deliveryLocationId ?? null;
	if (dlId == null || dlId === deliveryLocationId.value) return;
	deliveryLocationId.value = dlId;
	loadZone();
}

function handleCatchmentAreaUpdated(event) {
	if (event?.detail?.deliveryLocationId !== deliveryLocationId.value) return;
	getCatchmentAreaByDeliveryLocation(deliveryLocationId.value, true).then((updated) => {
		zone.value = updated;
	});
}

let _mountTarget = null;

onMounted(() => {
	window.addEventListener('resize', onResize);
	window.addEventListener('catchment-area-updated', handleCatchmentAreaUpdated);

	_mountTarget = zoneRef.value?.parentElement;
	_mountTarget?.addEventListener('vue:catchment-context', handleContextChange);
	if (_mountTarget?._pendingCatchmentContext) {
		handleContextChange({ detail: _mountTarget._pendingCatchmentContext });
	}
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', onResize);
	window.removeEventListener('catchment-area-updated', handleCatchmentAreaUpdated);
	_mountTarget?.removeEventListener('vue:catchment-context', handleContextChange);
	_mountTarget = null;
});
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.catchment-area-definition {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 16px;

	&__toolbar {
		display: flex;
		align-items: center;
		gap: 12px;

		@media (max-width: $tablet-breakpoint) {
			flex-wrap: wrap;
		}
	}

	&__search-wrap {
		flex: 1 1 260px;
		max-width: 420px;

		@media (max-width: $tablet-breakpoint) {
			flex-basis: 100%;
			max-width: none;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 8px;

		@media (max-width: $tablet-breakpoint) {
			flex-direction: column;
			align-items: flex-start;
		}

		.title {
			font-size: $font-size-large;
		}
	}
}
</style>
